import { Calculator, Fraction, UIUtil } from '@suntex.core/animation';
import { $Color, Color } from '@suntex.core/color';

export interface ValueText extends createjs.Container {
  fields: {
    largeText: createjs.Text;
    smallText: createjs.Text;
    tinyText: createjs.Text;
    extraTinyText: createjs.Text;
    numerator: createjs.Text;
    denominator: createjs.Text;
    line: createjs.Text;
  };
  textGlow: createjs.DisplayObject;
  /** Semantic value currently displayed, for screen-reader announcements. */
  a11yValue?: number | Fraction | string | null;
  /** Animate symbol design box, set on the symbol prototype. */
  nominalBounds?: createjs.Rectangle;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ValueText {
  const DECIMAL_PRECISION = 4;

  export enum Size {
    LARGE = 0,
    SMALL = 1,
    TINY = 2,
    EXTRA_TINY = 3,
  }

  export function init(valueText: ValueText) {
    clear(valueText);
    UIUtil.disable(valueText);
    // Text fields draw with an alphabetic baseline, so the container's computed
    // bounds sit above its visual center. Pin bounds to the symbol design box so
    // getBounds() (used by the a11y focus indicator) stays centered.
    const design = valueText.nominalBounds;
    if (design) {
      valueText.setBounds(design.x, design.y, design.width, design.height);
    }
  }

  export function clear(valueText: ValueText) {
    forEachTextField(valueText, (f) => {
      f.text = '';
      f.font = f.font.replace('Tahoma', 'Suntex').replace('Arial', 'Suntex');
    });
    valueText.fields.line.alpha = 0.0;
    valueText.textGlow.alpha = 0.0;
    valueText.a11yValue = null;
  }

  export function displayText(
    valueText: ValueText,
    text: string | number | undefined,
    size: Size = Size.LARGE,
  ) {
    clear(valueText);
    if (text !== undefined) {
      setFieldText(valueText, String(text), size);
      valueText.a11yValue = String(text);
    }
  }

  export function displayValue(
    valueText: ValueText,
    value: number | Fraction | null,
    size?: Size,
  ) {
    if (value == null) {
      clear(valueText);
    } else if (Calculator.isFraction(value)) {
      displayFraction(valueText, value);
    } else {
      displayDecimal(valueText, value, size);
    }
  }

  export function setColor(valueText: ValueText, color: Color) {
    forEachTextField(valueText, (f) => (f.color = $Color.toHexString(color)));
    UIUtil.setFrame(valueText.fields.line, 2);
  }

  export function whiten(valueText: ValueText) {
    setColor(valueText, $Color.from('#ffffff'));
  }

  /**
   * Speech-friendly form of the currently displayed value: integers and
   * decimals verbatim (with a leading minus spoken as "negative"), fractions as
   * "N over D". Returns an empty string when nothing is displayed.
   */
  export function getAccessibleText(valueText: ValueText): string {
    const value = valueText.a11yValue;
    if (value == null) return '';
    if (typeof value === 'string') return value;
    return describeValue(value);
  }

  /**
   * Speech-friendly form of a numeric value: integers and decimals verbatim
   * (with a leading minus spoken as "negative"), fractions as "N over D".
   */
  export function describeValue(value: number | Fraction): string {
    if (Calculator.isFraction(value)) {
      return `${formatNumber(value[0])} over ${value[1]}`;
    }
    return formatNumber(value);
  }

  function formatNumber(n: number): string {
    return n < 0 ? `negative ${Math.abs(n)}` : `${n}`;
  }

  /**
   * Stacks two lines of text in the numerator/denominator fields, leaving the
   * fraction line hidden. Used by tiles that show a pair of equations rather
   * than a fraction.
   */
  export function displayTwoLines(
    valueText: ValueText,
    topLine: string,
    bottomLine: string,
  ) {
    clear(valueText);
    valueText.fields.numerator.text = topLine;
    valueText.fields.denominator.text = bottomLine;
    valueText.a11yValue = [topLine, bottomLine].filter(Boolean).join(', ');
  }

  function forEachTextField(
    valueText: ValueText,
    action: (textField: createjs.Text) => void,
  ) {
    action(valueText.fields.largeText);
    action(valueText.fields.smallText);
    action(valueText.fields.tinyText);
    action(valueText.fields.extraTinyText);
    action(valueText.fields.numerator);
    action(valueText.fields.denominator);
  }

  function displayDecimal(
    valueText: ValueText,
    value: number,
    size?: ValueText.Size,
  ) {
    clear(valueText);
    value = Calculator.round(value, DECIMAL_PRECISION);
    const numDigits = getDigitCount(value);
    size = size ?? Calculator.bound(numDigits - 1, 0, Size.EXTRA_TINY);
    setFieldText(valueText, '' + value, size);
    valueText.a11yValue = value;
  }

  function displayFraction(valueText: ValueText, fraction: Fraction) {
    clear(valueText);
    if (fraction[1] == 1) {
      displayDecimal(valueText, fraction[0]);
    } else {
      valueText.fields.numerator.text = '' + fraction[0];
      valueText.fields.denominator.text = '' + fraction[1];
      valueText.fields.line.alpha = 1.0;
      valueText.a11yValue = fraction;
    }
  }

  function getDigitCount(n: number): number {
    return getRegExpCount('' + n, /[-0-9]/);
  }

  function getRegExpCount(str: string, regexp: RegExp) {
    return str.split(regexp).length - 1;
  }

  function setFieldText(
    valueText: ValueText,
    text: string,
    size: ValueText.Size,
  ) {
    let textField: createjs.Text;
    switch (size) {
      case ValueText.Size.EXTRA_TINY:
        textField = valueText.fields.extraTinyText;
        break;
      case ValueText.Size.TINY:
        textField = valueText.fields.tinyText;
        break;
      case ValueText.Size.SMALL:
        textField = valueText.fields.smallText;
        break;
      default:
        textField = valueText.fields.largeText;
        break;
    }
    textField.text = text;
  }
}
